import { useRef, useEffect, useState, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { ConversationState } from '../types';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import ChatAutocompleteInput from './ChatAutocompleteInput';
import ChatDateTimeInput from './ChatDateTimeInput';

interface AgentChatProps {
  state: ConversationState;
  progress: number;
  onUserInput: (input: string) => void;
  onExport: () => void;
  onRestart: () => void;
}

export default function AgentChat({ state, progress, onUserInput, onExport, onRestart }: AgentChatProps) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.isTyping]);

  useEffect(() => {
    if (!state.isTyping) {
      inputRef.current?.focus();
    }
  }, [state.isTyping]);

  const handleSend = () => {
    const val = inputValue.trim();
    if (!val || state.isTyping) return;
    setInputValue('');
    onUserInput(val);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (value: string, _label: string) => {
    if (state.isTyping) return;
    if (value === '__export__') { onExport(); return; }
    if (value === '__restart__') { onRestart(); return; }
    onUserInput(value);
  };

  const showProgress = state.phase === 'collecting' && state.steps.length > 0;
  const currentStep = state.phase === 'collecting' ? state.steps[state.currentStepIndex] : undefined;
  const hasAutocomplete = !!(currentStep?.options && currentStep.options.length > 0);
  const hasDateTime = !!(currentStep?.isDateTime);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.contrastText' }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
              Agente de Promoções
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {state.isTyping ? 'Digitando...' : 'Online'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Exportar JSON">
            <span>
              <IconButton
                size="small"
                onClick={onExport}
                disabled={state.phase !== 'complete' && state.phase !== 'review'}
                sx={{ color: 'text.secondary' }}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Recomeçar">
            <IconButton size="small" onClick={onRestart} sx={{ color: 'text.secondary' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showProgress && (
        <Box sx={{ px: 2.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progresso do briefing
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{fontWeight: 600}}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'scroll',
          px: 2.5,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.300',
            borderRadius: 3,
            '&:hover': {
              bgcolor: 'grey.400',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: (theme) => `${theme.palette.grey[300]} transparent`,
        }}
      >
        {state.messages.map((msg, idx) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            onQuickReply={handleQuickReply}
            isLast={idx === state.messages.length - 1}
          />
        ))}
        {state.isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {hasDateTime ? (
          <ChatDateTimeInput
            key={currentStep?.fieldKey}
            disabled={state.isTyping}
            onSend={(value) => {
              if (!value || state.isTyping) return;
              onUserInput(value);
            }}
          />
        ) : hasAutocomplete ? (
          <ChatAutocompleteInput
            key={currentStep?.fieldKey}
            options={currentStep!.options!}
            multiple={currentStep!.multiple ?? false}
            disabled={state.isTyping}
            onSend={(value) => {
              if (!value || state.isTyping) return;
              onUserInput(value);
            }}
          />
        ) : (
          <>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              multiline
              maxRows={3}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={state.isTyping ? 'Aguarde...' : 'Digite sua resposta...'}
              disabled={state.isTyping}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem',
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() || state.isTyping}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                width: 36,
                height: 36,
                flexShrink: 0,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
}

